/* eslint-disable no-underscore-dangle, camelcase */
/**
 * Поиск зоны ограничения скорости по ID
 * Ищет в слое martin.speed_limits_mvt (заполненные полигоны зон скорости)
 *
 * Ищет по полю: id
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {string} speedLimitId - ID зоны для поиска
 * @returns {Object|null} { id, speed_limit, lat, lon, feature } или null если не найдена
 */
export function searchSpeedLimitInTiles (map, speedLimitId) {
    if (!map || !speedLimitId) {
        return null;
    }

    const sourceId = 'Зоны ограничения скорости_source';
    const sourceLayer = 'martin.speed_limits_mvt';

    try {
        // Проверяем наличие источника на карте
        const source = map.getSource(sourceId);
        if (!source) {
            return null;
        }

        // Получаем features из источника по фильтру ID
        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
            filter: [
                '==',
                [ 'to-string', [ 'get', 'id' ] ],
                String(speedLimitId),
            ],
        });

        if (features.length === 0) {
            return null;
        }

        // Берём первый найденный feature (полигон)
        const feature = features[0];
        const props = feature.properties;
        const geom = feature.geometry;

        // Вычисляем центр полигона (среднее значение координат)
        let lat = null;
        let lon = null;

        if (geom) {
            if (geom.type === 'Polygon' && geom.coordinates && geom.coordinates.length > 0) {
                const coords = geom.coordinates[0];
                const lats = coords.map(c => c[1]);
                const lons = coords.map(c => c[0]);
                lat = (Math.min(...lats) + Math.max(...lats)) / 2;
                lon = (Math.min(...lons) + Math.max(...lons)) / 2;
            } else if (geom.type === 'MultiPolygon' && geom.coordinates && geom.coordinates.length > 0) {
                const allCoords = geom.coordinates.flat(2);
                const lats = allCoords.map(c => c[1]);
                const lons = allCoords.map(c => c[0]);
                lat = (Math.min(...lats) + Math.max(...lats)) / 2;
                lon = (Math.min(...lons) + Math.max(...lons)) / 2;
            }
        }

        return {
            id: props.id,
            speed_limit: props.group_,
            lat,
            lon,
            feature,
            featureId: feature.id,
        };
    } catch (err) {
        return null;
    }
}

/**
 * Выделить зону скорости на карте
 * Увеличивает прозрачность и добавляет красный контур (outline)
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {Object} result - Результат от searchSpeedLimitInTiles
 * @returns {void}
 */
export function highlightSpeedLimit (map, result) {
    if (!map || !result) {
        return;
    }

    const { lat, lon, id, feature } = result;

    // Выделить на карте:
    // 1. Увеличиваем прозрачность полигона
    map.setPaintProperty('Зоны ограничения скорости', 'fill-opacity', [
        'case',
        [ '==', [ 'get', 'id' ], id ],
        0.9,    // Более прозрачная для найденной
        0.5,    // Нормальная для остальных
    ]);

    // 2. Добавляем красный контур (outline) для найденной зоны
    map.setPaintProperty('Зоны ограничения скорости_обводка', 'line-color', [
        'case',
        [ '==', [ 'get', 'id' ], id ],
        '#FF0000',  // Красный для найденной
        '#CCCCCC',  // Серый для остальных (исходный цвет обводки)
    ]);

    map.setPaintProperty('Зоны ограничения скорости_обводка', 'line-width', [
        'case',
        [ '==', [ 'get', 'id' ], id ],
        3,    // Толще для найденной
        0.5,  // Тонкий для остальных
    ]);

    // Зумироваться к полигону с паддингом
    if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
        // Вычисляем bounding box полигона
        const coords = feature.geometry.coordinates[0];
        const lats = coords.map(c => c[1]);
        const lons = coords.map(c => c[0]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);

        // FitBounds с паддингом
        map.fitBounds(
            [ [ minLon, minLat ], [ maxLon, maxLat ] ],
            { padding: 100, duration: 1000 }
        );
    } else if (feature && feature.geometry && feature.geometry.type === 'MultiPolygon') {
        // Для MultiPolygon берем все координаты
        const allCoords = feature.geometry.coordinates.flat(2);
        const lats = allCoords.map(c => c[1]);
        const lons = allCoords.map(c => c[0]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);

        map.fitBounds(
            [ [ minLon, minLat ], [ maxLon, maxLat ] ],
            { padding: 100, duration: 1000 }
        );
    } else if (lat && lon) {
        // Fallback на обычный flyTo если геометрия не найдена
        map.flyTo({
            center: [ lon, lat ],
            zoom: 13,
            duration: 1000,
            padding: 50,
        });
    }
}

/**
 * Очистить выделение зоны скорости
 * Восстанавливает исходное состояние слоя
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @returns {void}
 */
export function clearSpeedLimitHighlight (map) {
    if (!map) {
        return;
    }

    // Восстановить исходное состояние fill слоя
    map.setPaintProperty('Зоны ограничения скорости', 'fill-opacity', [
        'interpolate',
        [ 'linear' ],
        [ 'zoom' ],
        11,
        0.5,
        15,
        0.2,
    ]);

    // Восстановить исходное состояние outline слоя
    map.setPaintProperty('Зоны ограничения скорости_обводка', 'line-color', '#CCCCCC');
    map.setPaintProperty('Зоны ограничения скорости_обводка', 'line-width', 0.5);
}

/**
 * Получить все зоны скорости из загруженных тайлов
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @returns {Array} Массив объектов зон { id, speed_limit, lat, lon, ... }
 */
export function getAllSpeedLimitsFromTiles (map) {
    if (!map) {
        return [];
    }

    const sourceId = 'Зоны ограничения скорости_source';
    const sourceLayer = 'martin.speed_limits_mvt';

    try {
        const source = map.getSource(sourceId);
        if (!source) {
            return [];
        }

        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
        });

        return features.map(feature => {
            const props = feature.properties;
            const geom = feature.geometry;
            let lat = null;
            let lon = null;

            if (geom) {
                if (geom.type === 'Polygon' && geom.coordinates && geom.coordinates.length > 0) {
                    const coords = geom.coordinates[0];
                    const lats = coords.map(c => c[1]);
                    const lons = coords.map(c => c[0]);
                    lat = (Math.min(...lats) + Math.max(...lats)) / 2;
                    lon = (Math.min(...lons) + Math.max(...lons)) / 2;
                } else if (geom.type === 'MultiPolygon' && geom.coordinates && geom.coordinates.length > 0) {
                    const allCoords = geom.coordinates.flat(2);
                    const lats = allCoords.map(c => c[1]);
                    const lons = allCoords.map(c => c[0]);
                    lat = (Math.min(...lats) + Math.max(...lats)) / 2;
                    lon = (Math.min(...lons) + Math.max(...lons)) / 2;
                }
            }

            return {
                id: props.id,
                speed_limit: props.group_,
                lat,
                lon,
                feature,
                featureId: feature.id,
            };
        });
    } catch (err) {
        return [];
    }
}
